import { useMemo } from 'react';
import type { DistributiveOmit } from 'react-redux';
import { useAppSelector } from '@/shared/model';
import {
  APP_STORE_CONNECT_REPORTING_INTEGRATION_ID,
  APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID,
  APPLOVIN_ADS_REPORTING_INTEGRATION_ID,
  GOOGLE_ADS_REPORTING_INTEGRATION_ID,
  GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID,
  META_ADS_REPORTING_INTEGRATION_ID,
  PAY_PAL_REPORTING_INTEGRATION_ID,
  SNAPCHAT_ADS_REPORTING_INTEGRATION_ID,
  STRIPE_REPORTING_INTEGRATION_ID,
  TIK_TOK_FOR_BUSINESS_REPORTING_INTEGRATION_ID,
  UNITY_ADS_REPORTING_INTEGRATION_ID,
  MOLOCO_ADS_REPORTING_INTEGRATION_ID,
  IRON_SOURCE_REPORTING_INTEGRATION_ID,
  X_ADS_REPORTING_INTEGRATION_ID,
  LIFTOFF_REPORTING_INTEGRATION_ID,
  APPSFLYER_REPORTING_INTEGRATION_ID,
  GOOGLE_ANALYTICS_REPORTING_INTEGRATION_ID,
  MINTEGRAL_REPORTING_INTEGRATION_ID,
  APPLOVIN_MAX_REPORTING_INTEGRATION_ID,
  TAPJOY_REPORTING_INTEGRATION_ID,
  PINTEREST_REPORTING_INTEGRATION_ID,
  MICROSOFT_ADS_REPORTING_INTEGRATION_ID,
  SOLIDGATE_REPORTING_INTEGRATION_ID,
  ADJUST_REPORTING_INTEGRATION_ID,
  PADDLE_REPORTING_INTEGRATION_ID,
  VOLUUM_REPORTING_INTEGRATION_ID,
  PAY_PRO_REPORTING_INTEGRATION_ID,
  MERCHANTO_REPORTING_INTEGRATION_ID,
  CUSTOM_REPORTING_INTEGRATION_ID,
  PROPELLER_ADS_REPORTING_INTEGRATION_ID,
  EVERFLOW_REPORTING_INTEGRATION_ID,
  SINGULAR_REPORTING_INTEGRATION_ID,
  IMPACT_REPORTING_INTEGRATION_ID,
  BIGO_ADS_REPORTING_INTEGRATION_ID,
  CHARGEBEE_REPORTING_INTEGRATION_ID,
  HILLTOP_ADS_REPORTING_INTEGRATION_ID,
  TRAFFIC_STARS_REPORTING_INTEGRATION_ID,
  TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID,
  SCALEO_REPORTING_INTEGRATION_ID,
  BRANCH_REPORTING_INTEGRATION_ID,
  REDDIT_REPORTING_INTEGRATION_ID,
  XSOLLA_REPORTING_INTEGRATION_ID,
  AFFISE_REPORTING_INTEGRATION_ID,
  DISPUTEUR_REPORTING_INTEGRATION_ID,
  REDTRACK_REPORTING_INTEGRATION_ID,
  FUNNELFOX_REPORTING_INTEGRATION_ID,
  PRIMER_REPORTING_INTEGRATION_ID,
} from './constants';
import { getDatasourceStatus } from './getDatasourceStatus';
import { getIntegrationStatus } from './getIntegrationStatus';
import { selectIntegrations } from './slice';
import type { Datasource, Integration } from './types';
import { DatasourceType } from './types';

export enum DatasourceIds {
  adjust = 'adjust',
  affise = 'affise',
  appStoreConnect = 'app-store-connect',
  appLovinAds = 'app-lovin-ads',
  appLovinMax = 'app-lovin-max',
  appleSearchAds = 'apple-search-ads',
  appsflyer = 'appsflyer',
  bigoAds = 'bigo-ads',
  branch = 'branch',
  chargebee = 'chargebee',
  custom = 'custom',
  everflow = 'everflow',
  disputeur = 'disputeur',
  funnelfox = 'funnelfox',
  googleAds = 'google-ads',
  googleAnalytics = 'google-analytics',
  googlePlayConsole = 'google-play-console',
  hilltopAds = 'hilltop-ads',
  impact = 'impact',
  ironSource = 'iron-source',
  liftoff = 'liftoff',
  merchanto = 'merchanto',
  metaAds = 'meta-ads',
  microsoftAds = 'microsoft-ads',
  mintegral = 'mintegral',
  molocoAds = 'moloco-ads',
  paddle = 'paddle',
  payPal = 'pay-pal',
  payPro = 'pay-pro',
  pinterest = 'pinterest',
  primer = 'primer',
  propellerAds = 'propeller-ads',
  reddit = 'reddit',
  redtrack = 'redtrack',
  scaleo = 'scaleo',
  singular = 'singular',
  snapchatAds = 'snapchat-ads',
  solidgate = 'solidgate',
  stripe = 'stripe',
  tapJoy = 'tap-joy',
  tikTokForBusiness = 'tik-tok-for-business',
  trafficJunky = 'traffic-junky',
  trafficStars = 'traffic-stars',
  unityAds = 'unity-ads',
  voluum = 'voluum',
  xAds = 'x-ads',
  xsolla = 'xsolla',
}

const AVAILABLE_DATASOURCES: DistributiveOmit<Datasource, 'status'>[] = [
  {
    id: DatasourceIds.adjust,
    name: 'Adjust',
    sourceIntegrationIds: [ADJUST_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/adjust-logo.png',
  },
  {
    id: DatasourceIds.affise,
    name: 'Affise',
    sourceIntegrationIds: [AFFISE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.affiliateNetwork,
    iconUrl: '/images/datasourceIcons/affise-logo.png',
  },
  {
    id: DatasourceIds.appStoreConnect,
    name: 'App Store Connect',
    sourceIntegrationIds: [APP_STORE_CONNECT_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/app-store-connect-logo.png',
  },
  {
    id: DatasourceIds.appleSearchAds,
    name: 'Apple Search Ads',
    sourceIntegrationIds: [APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/apple-search-ads.png',
  },
  {
    id: DatasourceIds.appLovinAds,
    name: 'AppLovin Ads',
    sourceIntegrationIds: [APPLOVIN_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/app-lovin-logo.png',
  },
  {
    id: DatasourceIds.appLovinMax,
    name: 'AppLovin MAX',
    sourceIntegrationIds: [APPLOVIN_MAX_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/app-lovin-max-logo.png',
  },
  {
    id: DatasourceIds.appsflyer,
    name: 'AppsFlyer',
    sourceIntegrationIds: [APPSFLYER_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/appsflyer-logo.png',
  },
  {
    id: DatasourceIds.bigoAds,
    name: 'BIGO Ads',
    sourceIntegrationIds: [BIGO_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/bigo-ads-logo.png',
  },
  {
    id: DatasourceIds.branch,
    name: 'Branch',
    sourceIntegrationIds: [BRANCH_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/branch-logo.png',
  },
  {
    id: DatasourceIds.chargebee,
    name: 'Chargebee',
    sourceIntegrationIds: [CHARGEBEE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/chargebee-logo.png',
  },
  {
    id: DatasourceIds.disputeur,
    name: 'Disputeur',
    sourceIntegrationIds: [DISPUTEUR_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/disputeur-logo.png',
  },
  {
    id: DatasourceIds.everflow,
    name: 'Everflow',
    sourceIntegrationIds: [EVERFLOW_REPORTING_INTEGRATION_ID],
    type: DatasourceType.affiliateNetwork,
    iconUrl: '/images/datasourceIcons/everflow-logo.png',
  },
  {
    id: DatasourceIds.funnelfox,
    name: 'FunnelFox',
    sourceIntegrationIds: [FUNNELFOX_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/funnelfox-logo.png',
  },
  {
    id: DatasourceIds.googleAds,
    name: 'Google Ads',
    sourceIntegrationIds: [GOOGLE_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/google-ads.png',
  },
  {
    id: DatasourceIds.googleAnalytics,
    name: 'Google Analytics',
    sourceIntegrationIds: [GOOGLE_ANALYTICS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/google-analytics-logo.png',
  },
  {
    id: DatasourceIds.googlePlayConsole,
    name: 'Google Play Console',
    sourceIntegrationIds: [GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/google-play-console-logo.png',
  },
  {
    id: DatasourceIds.hilltopAds,
    name: 'Hilltop Ads',
    sourceIntegrationIds: [HILLTOP_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/hilltop-ads-logo.png',
  },
  {
    id: DatasourceIds.impact,
    name: 'Impact',
    sourceIntegrationIds: [IMPACT_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/impact-logo.png',
  },
  {
    id: DatasourceIds.ironSource,
    name: 'ironSource',
    sourceIntegrationIds: [IRON_SOURCE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/ironsource-logo.png',
  },
  {
    id: DatasourceIds.liftoff,
    name: 'liftoff',
    sourceIntegrationIds: [LIFTOFF_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/liftoff-logo.png',
  },
  {
    id: DatasourceIds.merchanto,
    name: 'Merchanto',
    sourceIntegrationIds: [MERCHANTO_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/merchanto-logo.png',
  },
  {
    id: DatasourceIds.metaAds,
    name: 'Meta Ads',
    sourceIntegrationIds: [META_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/meta.png',
  },
  {
    id: DatasourceIds.microsoftAds,
    name: 'Microsoft Advertising',
    sourceIntegrationIds: [MICROSOFT_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/microsoft-ads-logo.png',
  },
  {
    id: DatasourceIds.mintegral,
    name: 'Mintegral',
    sourceIntegrationIds: [MINTEGRAL_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/mintegral-logo.png',
  },
  {
    id: DatasourceIds.molocoAds,
    name: 'Moloco Ads',
    sourceIntegrationIds: [MOLOCO_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/moloco-ads-logo.png',
  },
  {
    id: DatasourceIds.paddle,
    name: 'Paddle',
    sourceIntegrationIds: [PADDLE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/paddle-logo.png',
  },
  {
    id: DatasourceIds.payPal,
    name: 'PayPal',
    sourceIntegrationIds: [PAY_PAL_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/pay-pal-logo.png',
  },
  {
    id: DatasourceIds.payPro,
    name: 'PayPro',
    sourceIntegrationIds: [PAY_PRO_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/paypro-logo.png',
  },
  {
    id: DatasourceIds.pinterest,
    name: 'Pinterest',
    sourceIntegrationIds: [PINTEREST_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/pinterest-logo.png',
  },
  {
    id: DatasourceIds.primer,
    name: 'Primer',
    sourceIntegrationIds: [PRIMER_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/primer-logo.png',
  },
  {
    id: DatasourceIds.propellerAds,
    name: 'Propeller Ads',
    sourceIntegrationIds: [PROPELLER_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/propeller-ads-logo.png',
  },
  {
    id: DatasourceIds.reddit,
    name: 'Reddit',
    sourceIntegrationIds: [REDDIT_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/reddit-logo.png',
  },
  {
    id: DatasourceIds.redtrack,
    name: 'RedTrack',
    sourceIntegrationIds: [REDTRACK_REPORTING_INTEGRATION_ID],
    type: DatasourceType.affiliateNetwork,
    iconUrl: '/images/datasourceIcons/redtrack-logo.png',
  },
  {
    id: DatasourceIds.scaleo,
    name: 'Scaleo',
    sourceIntegrationIds: [SCALEO_REPORTING_INTEGRATION_ID],
    type: DatasourceType.affiliateNetwork,
    iconUrl: '/images/datasourceIcons/scaleo-logo.png',
  },
  {
    id: DatasourceIds.singular,
    name: 'Singular',
    sourceIntegrationIds: [SINGULAR_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/singular-logo.png',
  },
  {
    id: DatasourceIds.snapchatAds,
    name: 'Snapchat Ads',
    sourceIntegrationIds: [SNAPCHAT_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/snapchat-logo.png',
  },
  {
    id: DatasourceIds.solidgate,
    name: 'Solidgate',
    sourceIntegrationIds: [SOLIDGATE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/solidgate-logo.png',
  },
  {
    id: DatasourceIds.stripe,
    name: 'Stripe',
    sourceIntegrationIds: [STRIPE_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/stripe-logo.png',
  },
  {
    id: DatasourceIds.tapJoy,
    name: 'TapJoy',
    sourceIntegrationIds: [TAPJOY_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/tapjoy-logo.png',
  },
  {
    id: DatasourceIds.tikTokForBusiness,
    name: 'TikTok for Business',
    sourceIntegrationIds: [TIK_TOK_FOR_BUSINESS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/tiktok-for-business.png',
  },
  {
    id: DatasourceIds.trafficJunky,
    name: 'TrafficJunky',
    sourceIntegrationIds: [TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/traffic-junky-logo.png',
  },
  {
    id: DatasourceIds.trafficStars,
    name: 'TrafficStars',
    sourceIntegrationIds: [TRAFFIC_STARS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/traffic-stars-logo.png',
  },
  {
    id: DatasourceIds.unityAds,
    name: 'Unity Ads',
    sourceIntegrationIds: [UNITY_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/unity-ads-logo.png',
  },
  {
    id: DatasourceIds.voluum,
    name: 'Voluum',
    sourceIntegrationIds: [VOLUUM_REPORTING_INTEGRATION_ID],
    type: DatasourceType.attribution,
    iconUrl: '/images/datasourceIcons/voluum-logo.png',
  },
  {
    id: DatasourceIds.xAds,
    name: 'X Ads (Twitter Ads)',
    sourceIntegrationIds: [X_ADS_REPORTING_INTEGRATION_ID],
    type: DatasourceType.marketingSpend,
    iconUrl: '/images/datasourceIcons/x-ads-logo.png',
  },
  {
    id: DatasourceIds.xsolla,
    name: 'Xsolla',
    sourceIntegrationIds: [XSOLLA_REPORTING_INTEGRATION_ID],
    type: DatasourceType.monetization,
    iconUrl: '/images/datasourceIcons/xsolla-logo.png',
  },
  {
    id: DatasourceIds.custom,
    name: 'Custom Integration',
    sourceIntegrationIds: [CUSTOM_REPORTING_INTEGRATION_ID],
    iconUrl: '/images/datasourceIcons/custom-logo.png',
  },
];

export const useDatasources = (): Datasource[] => {
  const integrations = useAppSelector(selectIntegrations);
  const datasourcesWithStatus = useMemo(() => {
    const integrationsMap = integrations.reduce<Record<string, Integration[]>>(
      (acc, integration) => {
        if (!acc[integration.sourceIntegrationId]) {
          acc[integration.sourceIntegrationId] = [];
        }

        acc[integration.sourceIntegrationId].push(integration);

        return acc;
      },
      {},
    );

    return AVAILABLE_DATASOURCES.map((datasource) => {
      const relatedIntegrations = datasource.sourceIntegrationIds
        .map((id) => {
          return integrationsMap[id] || [];
        })
        .flat();
      const integrationStatuses = relatedIntegrations.map((integration) => {
        return getIntegrationStatus(integration);
      });

      return {
        ...datasource,
        status: getDatasourceStatus(integrationStatuses),
      };
    });
  }, [integrations]);

  return datasourcesWithStatus;
};
